import React, { Fragment, useContext, useEffect, useState } from 'react';
import { Table } from 'reactstrap';
import { Link } from 'react-router-dom';
import i18n, { getDateFormatOptions } from '@/utils/i18n';
import { OrderFulfillmentDetailFragment } from '../../../../graphql/fragments/orderFulfillmentDetail.generated';
import { OrderFulfillmentContainerLocationDetailFragment } from '../../../../graphql/fragments/orderFulfillmentContainerLocationDetail.generated';
import { DEFAULT_CONTAINER_TYPE_ICON, getImageUrl } from '@/utils/imageUrl';
import {
  IsVytalEmployee,
  ViewerContext,
} from '../../../../providers/ViewerContextProvider';
import { Communicator } from '../../../../tools/Communication';

interface OrderFulfillmentSingleContainersTableProps {
  orderFulfillment: OrderFulfillmentDetailFragment;
  allowDelete?: boolean;
}

const OrderFulfillmentSingleContainersTable = ({
  orderFulfillment,
}: OrderFulfillmentSingleContainersTableProps) => {
  const [packingUnits, setPackingUnits] = useState<Record<number, number>>({});
  const viewer = useContext(ViewerContext);
  const isInternal = IsVytalEmployee(viewer);

  useEffect(() => {
    Communicator.Get('api/3/Container/GetPackagingUnits', {})
      .then((result) => result.json())
      .then((result) => {
        setPackingUnits(result);
      });
  }, []);

  function groupContainersByType(
    containers: OrderFulfillmentContainerLocationDetailFragment[],
  ): Record<
    number,
    (OrderFulfillmentContainerLocationDetailFragment & {
      amount?: number;
      sealed?: boolean;
    })[]
  > {
    const containersByType = containers.reduce(
      (acc, container) => {
        if (!acc[container.container_type_id]) {
          acc[container.container_type_id] = [];
        }

        acc[container.container_type_id].push(container);

        return acc;
      },
      {} as Record<
        number,
        (OrderFulfillmentContainerLocationDetailFragment & {
          amount?: number;
        })[]
      >,
    );

    return containersByType;
  }

  const groupedContainersByType = groupContainersByType(
    orderFulfillment.single_containers,
  );

  const groupedContainersByTypeEntries = Object.entries(
    groupedContainersByType,
  );

  return (
    <>
      {groupedContainersByTypeEntries.length === 0 && '-'}
      {groupedContainersByTypeEntries.length > 0 && (
        <Table striped hover bordered>
          <thead>
            <tr>
              <th>{i18n.t('Common:containerType')}</th>
              <th>{i18n.t('Common:amount')}</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {groupedContainersByTypeEntries.map(([typeId, containers]) => {
              const firstContainer = containers.at(0);
              const amount = containers.length;

              return (
                <Fragment key={typeId}>
                  {containers.length === 0 && (
                    <>
                      <tr>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                        <td>-</td>
                      </tr>
                    </>
                  )}
                  <tr>
                    <td>
                      <img
                        style={{
                          width: '2rem',
                          height: '2rem',
                          marginRight: '0.5rem',
                        }}
                        src={getImageUrl(
                          50,
                          firstContainer?.container_type_image ||
                            DEFAULT_CONTAINER_TYPE_ICON,
                        )}
                        title={firstContainer?.container_type_name}
                        alt={firstContainer?.container_type_name}
                      />{' '}
                      {firstContainer?.container_type_name}
                    </td>

                    <td>{amount}</td>

                    <td>
                      <Table striped hover bordered>
                        <thead>
                          <tr>
                            <th>{i18n.t('Common:containerName')}</th>
                          </tr>
                        </thead>
                        <tbody>
                          {containers.map((container) => {
                            return (
                              <tr key={container.container_id}>
                                <td>
                                  <Link
                                    to={`/container/${container.container_id}`}
                                  >
                                    {container.container_name}
                                  </Link>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </Table>
                    </td>
                  </tr>
                </Fragment>
              );
            })}
          </tbody>
        </Table>
      )}
    </>
  );
};

export default OrderFulfillmentSingleContainersTable;
